import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Heart, Calendar, Plus, ChevronLeft, ChevronRight, Lock, Droplet, Sun, Moon, Frown, Meh, Smile, Zap, ThermometerSun } from 'lucide-react';

const CYCLE_PHASES = {
  menstruation: { label: 'Mens', color: 'bg-red-500', description: 'Dag 1-5: Vila och återhämtning' },
  follicular: { label: 'Follikelfas', color: 'bg-pink-400', description: 'Dag 6-13: Energin ökar' },
  ovulation: { label: 'Ägglossning', color: 'bg-purple-500', description: 'Dag 14-16: Högst energi' },
  luteal: { label: 'Lutealfas', color: 'bg-amber-500', description: 'Dag 17-28: Lugna ner' }
};

const SYMPTOMS = [
  { id: 'cramps', label: 'Kramper', icon: '😖' },
  { id: 'headache', label: 'Huvudvärk', icon: '🤕' },
  { id: 'fatigue', label: 'Trötthet', icon: '😴' },
  { id: 'bloating', label: 'Uppsvälld', icon: '🫃' },
  { id: 'mood_swings', label: 'Humörsvängningar', icon: '🎭' },
  { id: 'cravings', label: 'Sug', icon: '🍫' },
  { id: 'breast_tenderness', label: 'Öm bröst', icon: '💔' },
  { id: 'acne', label: 'Finnar', icon: '😣' },
];

const MOODS = [
  { id: 'great', label: 'Toppen', icon: Smile, color: 'text-green-500' },
  { id: 'okay', label: 'Okej', icon: Meh, color: 'text-amber-500' },
  { id: 'low', label: 'Låg', icon: Frown, color: 'text-red-500' },
];

const FLOW_LEVELS = [
  { id: 'light', label: 'Lätt', dots: 1 },
  { id: 'medium', label: 'Medium', dots: 2 },
  { id: 'heavy', label: 'Riklig', dots: 3 },
];

export default function HormoneTrackingPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [logs, setLogs] = useState({});
  const [cycleLength, setCycleLength] = useState(28);
  const [periodLength, setPeriodLength] = useState(5);
  const [lastPeriodStart, setLastPeriodStart] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const saved = localStorage.getItem('hormone-tracking');
    if (saved) {
      const data = JSON.parse(saved);
      setLogs(data.logs || {});
      setCycleLength(data.cycleLength || 28);
      setPeriodLength(data.periodLength || 5);
      setLastPeriodStart(data.lastPeriodStart ? new Date(data.lastPeriodStart) : null);
    }
  };

  const saveData = (newLogs = logs, newCycleLength = cycleLength, newPeriodLength = periodLength, newLastPeriod = lastPeriodStart) => {
    localStorage.setItem('hormone-tracking', JSON.stringify({
      logs: newLogs,
      cycleLength: newCycleLength,
      periodLength: newPeriodLength,
      lastPeriodStart: newLastPeriod?.toISOString()
    }));
  };

  const getDateKey = (date) => date.toISOString().split('T')[0];

  const getDayLog = (date) => logs[getDateKey(date)] || {};

  const updateDayLog = (field, value) => {
    const key = getDateKey(selectedDate);
    const newLogs = {
      ...logs,
      [key]: {
        ...logs[key],
        [field]: value
      }
    };
    setLogs(newLogs);
    
    // If marking period, update last period start
    if (field === 'isPeriod' && value && (!lastPeriodStart || selectedDate > lastPeriodStart)) {
      setLastPeriodStart(selectedDate);
      saveData(newLogs, cycleLength, periodLength, selectedDate);
    } else {
      saveData(newLogs);
    }
  };

  const toggleSymptom = (symptomId) => {
    const currentSymptoms = getDayLog(selectedDate).symptoms || [];
    const newSymptoms = currentSymptoms.includes(symptomId)
      ? currentSymptoms.filter(s => s !== symptomId)
      : [...currentSymptoms, symptomId];
    updateDayLog('symptoms', newSymptoms);
  };

  const getCycleDay = (date) => {
    if (!lastPeriodStart) return null;
    const diffTime = date - lastPeriodStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return null;
    return (diffDays % cycleLength) + 1;
  };

  const getPhase = (cycleDay) => {
    if (!cycleDay) return null;
    if (cycleDay <= periodLength) return 'menstruation';
    if (cycleDay <= 13) return 'follicular';
    if (cycleDay <= 16) return 'ovulation';
    return 'luteal';
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days = [];
    
    // Add padding
    for (let i = 0; i < startPadding; i++) {
      days.push(<div key={`pad-${i}`} className="h-10" />);
    }
    
    // Add days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const dayLog = getDayLog(date);
      const cycleDay = getCycleDay(date);
      const phase = getPhase(cycleDay);
      const isSelected = getDateKey(date) === getDateKey(selectedDate);
      const isToday = getDateKey(date) === getDateKey(new Date());
      
      days.push(
        <button
          key={d}
          onClick={() => setSelectedDate(date)}
          className={`h-10 w-full rounded-lg flex items-center justify-center relative transition-colors ${
            isSelected ? 'bg-primary text-primary-foreground' :
            isToday ? 'bg-primary/20' :
            dayLog.isPeriod ? 'bg-red-100' : 'hover:bg-secondary'
          }`}
        >
          <span className="text-sm font-medium">{d}</span>
          {dayLog.isPeriod && !isSelected && (
            <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-red-500" />
          )}
          {phase === 'ovulation' && !dayLog.isPeriod && (
            <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-purple-500" />
          )}
        </button>
      );
    }
    
    return days;
  };

  const handleBack = () => {
    navigate('/dashboard', { state: { expandCategory: 'body' } });
  };

  const todayCycleDay = getCycleDay(new Date());
  const todayPhase = getPhase(todayCycleDay);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="hormone-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-medium">Hormoncykel</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl font-medium">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground text-base">Hormoncykel-spårning ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full" data-testid="upgrade-btn">
              Uppgradera nu
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const selectedDayLog = getDayLog(selectedDate);
  const selectedCycleDay = getCycleDay(selectedDate);
  const selectedPhase = getPhase(selectedCycleDay);

  return (
    <div className="min-h-screen bg-background" data-testid="hormone-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-medium">Hormoncykel</h1>
          <Button 
            variant="ghost" 
            onClick={() => setShowSettings(!showSettings)}
            className="ml-auto rounded-full"
          >
            ⚙️
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Current Phase Banner */}
        {todayPhase && (
          <div className={`${CYCLE_PHASES[todayPhase].color} bg-opacity-20 rounded-2xl p-6 animate-fade-in`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full ${CYCLE_PHASES[todayPhase].color} flex items-center justify-center`}>
                <span className="text-white text-lg font-bold">{todayCycleDay}</span>
              </div>
              <div>
                <h2 className="font-heading text-xl font-medium">{CYCLE_PHASES[todayPhase].label}</h2>
                <p className="text-muted-foreground text-sm">{CYCLE_PHASES[todayPhase].description}</p>
              </div>
            </div>
          </div>
        )}

        {!lastPeriodStart && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
            <p className="text-amber-800 mb-3">Markera din senaste mensstart i kalendern för att börja spåra din cykel.</p>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-fade-in">
            <h3 className="font-heading text-lg font-medium">Inställningar</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Cykellängd (dagar)</label>
                <input
                  type="number"
                  value={cycleLength}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 28;
                    setCycleLength(val);
                    saveData(logs, val, periodLength, lastPeriodStart);
                  }}
                  className="w-full p-3 rounded-xl border border-border/50 bg-background text-base"
                  min="21"
                  max="35"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Menslängd (dagar)</label>
                <input
                  type="number"
                  value={periodLength}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 5;
                    setPeriodLength(val);
                    saveData(logs, cycleLength, val, lastPeriodStart);
                  }}
                  className="w-full p-3 rounded-xl border border-border/50 bg-background text-base"
                  min="2"
                  max="10"
                />
              </div>
            </div>
          </div>
        )}

        {/* Calendar */}
        <div className="bg-card rounded-2xl border border-border/50 p-6 animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
              className="rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h3 className="font-heading text-lg font-medium">
              {currentDate.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}
            </h3>
            <Button
              variant="ghost"
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
              className="rounded-full"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map(day => (
              <div key={day} className="h-8 flex items-center justify-center text-xs text-muted-foreground font-medium">
                {day}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-border/50 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" /> Mens
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-purple-500" /> Ägglossning
            </span>
          </div>
        </div>

        {/* Selected Day Details */}
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-medium">
                {selectedDate.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h3>
              {selectedCycleDay && (
                <p className="text-sm text-muted-foreground">
                  Cykeldag {selectedCycleDay} • {CYCLE_PHASES[selectedPhase]?.label}
                </p>
              )}
            </div>
          </div>

          {/* Period Toggle */}
          <div>
            <label className="block text-sm font-medium mb-3">Mens</label>
            <Button
              variant={selectedDayLog.isPeriod ? 'default' : 'outline'}
              onClick={() => updateDayLog('isPeriod', !selectedDayLog.isPeriod)}
              className="rounded-full"
            >
              <Droplet className={`w-4 h-4 mr-2 ${selectedDayLog.isPeriod ? 'fill-current' : ''}`} />
              {selectedDayLog.isPeriod ? 'Mens idag' : 'Markera mensdag'}
            </Button>
          </div>

          {/* Flow Level */}
          {selectedDayLog.isPeriod && (
            <div>
              <label className="block text-sm font-medium mb-3">Flöde</label>
              <div className="flex gap-2">
                {FLOW_LEVELS.map(level => (
                  <Button
                    key={level.id}
                    variant={selectedDayLog.flow === level.id ? 'default' : 'outline'}
                    onClick={() => updateDayLog('flow', level.id)}
                    className="rounded-full flex-1"
                  >
                    {'💧'.repeat(level.dots)} {level.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Mood */}
          <div>
            <label className="block text-sm font-medium mb-3">Humör</label>
            <div className="flex gap-2">
              {MOODS.map(mood => (
                <Button
                  key={mood.id}
                  variant={selectedDayLog.mood === mood.id ? 'default' : 'outline'}
                  onClick={() => updateDayLog('mood', mood.id)}
                  className="rounded-full flex-1"
                >
                  <mood.icon className={`w-5 h-5 mr-2 ${mood.color}`} />
                  {mood.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Symptoms */}
          <div>
            <label className="block text-sm font-medium mb-3">Symtom</label>
            <div className="flex flex-wrap gap-2">
              {SYMPTOMS.map(symptom => (
                <Button
                  key={symptom.id}
                  variant={(selectedDayLog.symptoms || []).includes(symptom.id) ? 'default' : 'outline'}
                  onClick={() => toggleSymptom(symptom.id)}
                  size="sm"
                  className="rounded-full"
                >
                  <span className="mr-1">{symptom.icon}</span>
                  {symptom.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Anteckningar</label>
            <textarea
              value={selectedDayLog.notes || ''}
              onChange={(e) => updateDayLog('notes', e.target.value)}
              placeholder="Skriv egna anteckningar..."
              className="w-full p-4 rounded-xl border border-border/50 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 text-base"
              rows={2}
            />
          </div>
        </div>

        {/* Phase Info */}
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="font-heading text-lg font-medium">Faserna i din cykel</h3>
          <div className="grid gap-3">
            {Object.entries(CYCLE_PHASES).map(([key, phase]) => (
              <div key={key} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30">
                <div className={`w-4 h-4 rounded-full ${phase.color}`} />
                <div>
                  <p className="font-medium text-sm">{phase.label}</p>
                  <p className="text-xs text-muted-foreground">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pt-8">
          <p className="text-sm text-muted-foreground">
            Att förstå din cykel hjälper dig leva i harmoni med din kropp.
          </p>
        </div>
      </main>
    </div>
  );
}
